'use client';

import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import type { ProductCardProps } from '@eliadmin/core/src/components/product/product-card';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { ProductListSchema } from '@eliadmin/core/src/components/seo/product-list-schema';
import type { CarouselProps } from '@eliadmin/core/src/components/ui/carousel';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMemo } from 'react';

export type ProductCarouselProps = Omit<CarouselProps, 'slides' | 'className'> & {
  products?: Product[] | null;
  className?: {
    carousel?: CarouselProps['className'];
    product?: ProductCardProps['className'];
  };
};

function getProductKey(product: Product) {
  return String(product.id ?? product.slug ?? product.code ?? product.name);
}

export function ProductCarousel({ className, products, ...props }: ProductCarouselProps) {
  const currency = useGlobalStore(s => s.generalLayout?.currency);
  const schemaCurrency = useGlobalStore(s => s.generalLayout?.currency_code);
  const orderedProducts = useMemo(
    () =>
      (Array.isArray(products) ? [...products] : []).sort((a, b) =>
        getProductKey(a).localeCompare(getProductKey(b)),
      ),
    [products],
  );

  const carouselProps = {
    ...props,
    options: {
      ...props.options,
      loop: true,
      dragFree: true,
    },
  };

  return orderedProducts.length > 0 ? (
    <>
      <ProductListSchema products={orderedProducts} currency={(schemaCurrency || currency)} />
      <Carousel
        className={{
          ...(className?.carousel as any),
          wrapper: cn('ea-product-carousel', className?.carousel?.wrapper),
          main: cn('w-full @container', className?.carousel?.main),
          container: cn('min-w-full', className?.carousel?.container),
          slide: cn(
            'basis-1/2 pl-2 first:pl-0 @2xl:basis-1/3 @5xl:basis-1/4',
            className?.carousel?.slide,
          ),
        }}
        slides={orderedProducts.map(product => (
          <ProductCard
            key={getProductKey(product)}
            product={product}
            className={{
              ...className?.product,
              main: cn('min-w-[100px]', className?.product?.main),
            }}
          />
        ))}
        {...carouselProps}
        options={{
          ...carouselProps.options,
          align: 'start',
          loop: false,
          dragFree: false,
        }}
      />
    </>
  ) : null;
}
